import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { Box, Theme, Typography, SxProps, useTheme } from '@mui/material';
import { shallow } from 'zustand/shallow';
import { ChevronDown, ChevronRight } from '@assets/svgs';
import { DrawerContent, DrawerHeader } from '@components/drawerItem';
import { useLayoutStore } from '@store/LayoutStore';
import { getPanelColor, icon } from '@components/quotePanel/QuotePanel.constants';
import { getPanelLabel } from '@pages/QuoteDetails/helpers';
import Loading from '@components/Loading';
import { useEffect, useMemo, useRef, useState, memo } from 'react';
import { PanelInstance } from '@store/Quote/Quote.types';
import { scrollToElement } from '@utils/scrollToElement';
import { useApiEnvStore } from '@utils/api-client';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { PinDrawer } from '@components/drawerItem/DrawerHeader';
import { QuoteDrawerUrlParams } from '@pages/QuoteDetails/constants/drawerListItems';

interface ExpandedIdMap {
  [key: string]: boolean;
}

export function QuoteTree() {
  const { quoteId } = useParams<{ quoteId: string }>();

  const { setHighlightedNavigationElementId } = useLayoutStore(
    (state) => ({
      setHighlightedNavigationElementId: state.setHighlightedNavigationElementId,
    }),
    shallow,
  );

  invariant(quoteId);

  const [panelInstances, panelInstancesByParentInstanceId] = useQuoteStore(
    (state) => [state.panelInstances, state.panelInstancesByParentInstanceId],
    shallow,
  );

  const [expandedIds, setExpandedIds] = useState<ExpandedIdMap>({ '': false });
  const activeScrollTarget = useRef<string | null>(null);

  useEffect(() => {
    const panelWithChildrenIds = Object.keys(panelInstancesByParentInstanceId);
    const panelsWithChildrenObj = panelWithChildrenIds.reduce((o, id) => ({ ...o, [id]: true }), {});
    if (panelWithChildrenIds.length > 1) {
      setExpandedIds(panelsWithChildrenObj as ExpandedIdMap);
    }
  }, [panelInstancesByParentInstanceId]);

  const expandedIdList = useMemo(() => {
    if (expandedIds) {
      return Object.entries(expandedIds)
        .filter((el) => el[1])
        .map((el) => el[0]);
    }
    return [];
  }, [expandedIds]);

  if (!panelInstances || panelInstances.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Loading />
      </Box>
    );
  }

  const topPanels = panelInstances.filter((panel) => !panel.parentPanelInstanceId);

  const handleSelect = (_: any, nodeId: string | null) => {
    if (!nodeId) {
      return;
    }

    const p = panelInstances.find((p) => p.panelInstanceId === nodeId);
    if (p) {
      setHighlightedNavigationElementId(p.panelInstanceId);
      activeScrollTarget.current = p.panelInstanceId;

      // Panels that haven't been scrolled to yet render with placeholder heights (80px per field).
      // The first scroll lands at the compressed position; as the intersection observer triggers
      // and panels load their real content, the target shifts further down. We retry the scroll
      // after a delay (enough for smooth scroll + lazy panel re-renders to settle) until the
      // element is visible in the viewport or we exhaust our attempts.
      const tryScroll = (panelInstanceId: string, attemptsLeft: number) => {
        if (activeScrollTarget.current !== panelInstanceId) return;

        const element = document.getElementById(panelInstanceId);
        if (!element) return;

        scrollToElement(element);

        if (attemptsLeft > 0) {
          setTimeout(() => {
            if (activeScrollTarget.current !== panelInstanceId) return;
            const rect = element.getBoundingClientRect();
            const isInView = rect.top >= -50 && rect.top < window.innerHeight;
            if (!isInView) {
              tryScroll(panelInstanceId, attemptsLeft - 1);
            }
          }, 350);
        }
      };

      tryScroll(p.panelInstanceId, 5);
    }
    if (Object.keys(expandedIds).includes(nodeId)) {
      setExpandedIds(
        (prev: ExpandedIdMap) =>
          ({
            ...prev,
            [nodeId]: !prev[nodeId],
          }) as ExpandedIdMap,
      );
    }
  };

  return (
    <>
      <DrawerHeader drawerTitle='Quote Navigator'>
        <PinDrawer urlParam={QuoteDrawerUrlParams.Navigator} />
      </DrawerHeader>
      <DrawerContent drawerColor='drawerNavigation.main' drawerPadding='8px 18px'>
        <SimpleTreeView
          tabIndex={0}
          slots={{
            expandIcon: ChevronRight,
            collapseIcon: ChevronDown,
          }}
          onSelectedItemsChange={handleSelect}
          expandedItems={expandedIdList}
          sx={{
            '& .MuiTreeItem-iconContainer': {
              svg: {
                width: '13.33px',
                height: '13.33px',
              },
            },
          }}
        >
          {topPanels.map((p, index) => (
            <TreeItemInstance
              sx={{ height: 'auto' }}
              key={p.panelInstanceId}
              panel={p}
              index={index}
              panelsByParentId={panelInstancesByParentInstanceId}
            />
          ))}
        </SimpleTreeView>
      </DrawerContent>
    </>
  );
}

const TreeItemInstance = memo(function TreeItemInstance({
  panel,
  panelsByParentId,
  index,
  sx,
}: {
  panel: PanelInstance;
  index?: number;
  panelsByParentId: { [key: string]: PanelInstance[] };
  sx: SxProps;
}) {
  const [debugMode, debugModeOptions] = useApiEnvStore((state) => [state.debugMode, state.debugModeOptions]);
  const showExtraDetails = debugMode && debugModeOptions.showExtraDetails;

  const activeItem = useLayoutStore(
    (store) =>
      (!store.highlightedNavigationElement && index === 0) ||
      store.highlightedNavigationElement === panel.panelInstanceId,
    shallow,
  );

  const { quoteId } = useParams<{ quoteId: string }>();
  const [panelStatus, panelIsShown] = useQuoteStore(
    (state) => [
      state.panelInstanceStatusById[panel.panelInstanceId],
      state.panelInstanceVisibilityById[panel.panelInstanceId],
    ],
    shallow,
  );

  const { palette } = useTheme<Theme>();
  const panelChildren = panelsByParentId[panel.panelInstanceId] ?? [];

  if (!quoteId || (!panelIsShown && !panelChildren.length)) {
    return null;
  }

  if (!panelIsShown) {
    return (
      <>
        {panelChildren.map((child) => (
          <TreeItemInstance
            sx={{ height: 'auto', ...sx }}
            panel={child}
            key={child.panelInstanceId}
            panelsByParentId={panelsByParentId}
          />
        ))}
      </>
    );
  }

  const color = getPanelColor(palette);
  const statusIcon = icon[panelStatus];
  const panelLabel = getPanelLabel(panel);

  const instanceNumber = panel.quoteItem.instanceNumber;

  return (
    <TreeItem
      tabIndex={0}
      itemId={panel.panelInstanceId}
      sx={{
        '& .MuiTreeItem-content': {
          padding: '0px 0px 0px 0px',
        },
      }}
      label={
        <Box
          sx={[
            {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              paddingY: '0px',
            },
            (theme) => ({
              alignItems: 'flex-start',

              svg: {
                color: panelStatus ? color[panelStatus].icon : theme.palette.text.disabled,

                padding: '0 2px',
                ml: '0px',
                mr: '0px',
                border: activeItem ? '1px solid #2C7B74' : '1px solid transparent',
                borderRadius: activeItem ? '20px' : 'none',
              },
            }),
          ]}
        >
          <Box
            sx={{
              height: 20,
              width: 20,
              mr: 0.5,
            }}
          >
            {statusIcon}
          </Box>
          <Typography
            variant='bodySmall'
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              color: ({ palette }: any) => (activeItem ? `${palette.secondary.main} !important` : 'text.main'),
            }}
          >
            {showExtraDetails && (
              <Typography component='span' variant='caption' color='text.subdued' sx={{ mr: 0.5 }}>
                ({instanceNumber})
              </Typography>
            )}
            {panelLabel}
          </Typography>
        </Box>
      }
    >
      {panelChildren.map((child) => (
        <TreeItemInstance
          sx={{ height: 'auto' }}
          panel={child}
          key={child.panelInstanceId}
          panelsByParentId={panelsByParentId}
        />
      ))}
    </TreeItem>
  );
});
